import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type Step = { title: string; width?: number };

interface WizardState<T = Record<string, unknown>> {
  // State
  steps: Step[];
  currentStep: number;
  formData: T;
  stepsGap: number | null;
  currentStepFormGetter: (() => Record<string, unknown>) | null;

  // Actions
  setSteps: (steps: Step[], initialStep?: number) => void;
  goTo: (idx: number) => void;
  setFormData: (data: Partial<T>) => void;
  next: (arg?: React.MouseEvent | (() => boolean)) => void;
  prev: () => void;
  reset: () => void;
  restart: () => void;
  setStepWidth: (index: number, width: number) => void;
  setStepsGap: (gap: number) => void;
  clearFormData: () => void;
  setCurrentStepFormGetter: (getter: (() => Record<string, unknown>) | null) => void;

  //Exit Dialog
  shouldShowExitDialog: boolean;
  toggleExitDialog: (isOpen: boolean) => void;
}

export const useWizardStore = create<WizardState>()(
  devtools(
    (set) => ({
      // State
      steps: [],
      currentStep: 0,
      formData: {},
      stepsGap: 0,
      currentStepFormGetter: null,

      // Actions
      setSteps: (steps, initialStep = 0) =>
        set({ steps, currentStep: initialStep }, undefined, 'setSteps'),

      next: (arg?: React.MouseEvent | (() => boolean)) => {
        if (typeof arg === 'function' && !arg()) {
          return;
        }

        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }

        return set(
          (state) => {
            const maxStep = state.steps.length;
            return { currentStep: Math.min(state.currentStep + 1, maxStep) };
          },
          undefined,
          'next'
        );
      },

      prev: () => {
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }
        return set(
          (state) => ({
            currentStep: Math.max(state.currentStep - 1, 0),
          }),
          undefined,
          'prev'
        );
      },
      goTo: (idx) => {
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }

        return set(
          (state) => ({
            currentStep: Math.max(0, Math.min(idx, state.steps.length + 1)), // Allow one extra step for draft success
          }),
          undefined,
          'goTo'
        );
      },
      setFormData: (data) =>
        set(
          (state) => ({
            formData: { ...state.formData, ...data },
          }),
          undefined,
          'setFormData'
        ),
      reset: () => set({ steps: [], currentStep: 0, formData: {} }, undefined, 'reset'),
      restart: () =>
        set(
          () => ({
            currentStep: 0,
            formData: {},
            currentStepFormGetter: null,
            shouldShowExitDialog: false,
          }),
          undefined,
          'restart'
        ),

      setStepWidth: (index, width) => {
        set(
          (state) => ({
            steps: state.steps.map((step, i) => (i === index ? { ...step, width } : step)),
          }),
          undefined,
          'setStepWidth'
        );
      },

      setStepsGap: (gap) => set({ stepsGap: gap }, undefined, 'setStepsGap'),

      clearFormData: () => set({ formData: {} }, undefined, 'clearFormData'),

      setCurrentStepFormGetter: (getter) =>
        set({ currentStepFormGetter: getter }, undefined, 'setCurrentStepFormGetter'),

      //Exit Dialog
      shouldShowExitDialog: false,
      toggleExitDialog: (isOpen: boolean) => set({ shouldShowExitDialog: isOpen }),
    }),
    {
      enabled: process.env.NODE_ENV === 'development',
      name: 'wizard-store',
    }
  )
);
